import { useState } from "react"
import { createPortal } from "react-dom"
import { AnimatePresence } from "framer-motion"
import styled from "@emotion/styled"

import { useEnv } from "contexts/Env"
import { useLocale } from "contexts/Locale"
import { useDictionary } from "contexts/Dictionary"
import { useUser } from "contexts/User"

import getMediaQuery from "css/breakpoints"
import { buttonReset } from "css/buttons"
import { body50, bold, legend100, legend80 } from "css/text"

import { getPriceDatas } from "components/price/utils"
import Link from "components/link/Link"
import Image from "components/media/ImageCLD"
import VideoLauncher from "components/video-launcher/VideoLauncher"
import { waTriggerEvent } from "../analytics/DigitalDataLayer"
import Modal from "./DownloadModal"
import { getModelData } from "./DownloadModal"

const Root = styled.li`
  position: relative;
  color: rgb(var(${props => (props.isBrochure ? "--light-black" : "--pure-white")}));
  background-color: rgb(var(${props => (props.isBrochure ? "--light-grey" : "--pure-white")}));

  .main-link {
    color: rgb(var(${props => (props.isBrochure ? "--green" : "--pure-white")})) !important;
  }
`

const Card = styled.a`
  ${buttonReset}
  display: block;
  cursor: pointer;
`

const ImageWrapper = styled.div`
  // Get an homotethic ratio of 0.5:1
  ${props =>
    props.downloadType === "wallpaper" &&
    `
    width: calc(var(--grid-col-unit) * 3 + var(--grid-gap) * 2);
    height: calc((var(--grid-col-unit) * 3 + var(--grid-gap) * 2) * 2);
  `}

  ${props =>
    props.downloadType === "brochure" &&
    `
    width: 100%:
    height: auto;
  `}

  ${props =>
    props.downloadType === "userGuide" &&
    `
    width: calc(var(--grid-col-unit) * 6 + var(--grid-gap) * 5);
    height: calc((var(--grid-col-unit) * 6 + var(--grid-gap) * 5) * 0.64);
  `}

  ${getMediaQuery("m")} {
    // Get an homotethic ratio of 1:1.57
    width: calc(var(--grid-col-unit) * 6 + var(--grid-gap) * 5);
    height: calc((var(--grid-col-unit) * 6 + var(--grid-gap) * 5) * 0.64);
  }

  ${getMediaQuery("xl")} {
    // Get an homotethic ratio of 1:1.57
    width: calc(var(--grid-col-unit) * 4 + var(--grid-gap) * 3);
    height: calc((var(--grid-col-unit) * 4 + var(--grid-gap) * 3) * 0.64);
  }

  img {
    object-fit: ${props => (props.downloadType === "brochure" ? "contain" : "cover")};

    height: ${props => (props.downloadType === "brochure" ? "100%" : "100%")};
    margin-top: ${props => (props.downloadType === "brochure" ? "10%" : "0")};
    width: 100%;

    ${getMediaQuery("m")} {
      height: 100%;
      width: ${props => (props.downloadType === "brochure" ? "48%" : "100%")};
      margin-left: ${props => (props.downloadType === "brochure" ? "52%" : "0")};
      margin-top: 0;
    }
  }
`

const Content = styled.div`
  min-height: 120px;
  position: ${props => (props.downloadType === "brochure" ? "static" : "absolute")};
  left: 0;
  right: 0;
  top: ${props => (props.downloadType === "userGuide" ? "0" : "65%")};
  bottom: ${props => (props.downloadType === "userGuide" ? "calc(3vw + 30px)" : "0")};
  padding-inline: 7%;
  display: flex;
  flex-direction: column;
  justify-content: ${props => (props.downloadType === "userGuide" ? "flex-end" : "flex-start")};
  text-align: ${props => (props.downloadType === "userGuide" ? "center" : "start")};
  margin-block-start: -20px;

  ${getMediaQuery("m")} {
    position: absolute;
    min-height: unset;
    right: ${props => (props.downloadType === "userGuide" ? "0" : "50%")};
    top: 0;
    bottom: ${props => (props.downloadType === "userGuide" ? "calc(3vw + 30px)" : "0")};
    justify-content: ${props => (props.downloadType === "userGuide" ? "flex-end" : "center")};
    text-align: ${props => (props.downloadType === "userGuide" ? "center" : "start")};
  }
`

const Title = styled.h2`
  ${body50}
  ${bold}
  line-height: 1.1;
  margin-block-end: 0.875ex;
`

const Description = styled.p`
  ${legend100}
  line-height: 1;
  &:lang(ar),
  &:lang(fa),
  &:lang(he),
  &:lang(hi),
  &:lang(ja),
  &:lang(ko),
  &:lang(th),
  &:lang(vi),
  &:lang(zh-hant),
  &:lang(zh-hans) {
    line-height: 1.143;
  }
`

const CTAS = styled.div`
  position: absolute;
  bottom: 0;
  padding-block-end: 3vw;
  padding-inline: 7%;
  display: flex;
  gap: 2rem;

  left: ${props => (props.userGuide ? "50%" : "0")};
  transform: ${props => (props.userGuide ? "translateX(-50%)" : "none")};

  // ${getMediaQuery("m", { max: true })} {
  //   padding-inline: 0;
  //   display: block;
  //   text-align: start;
  //   width: calc(100% - 14%);
  //   padding: 0 7%;
  // }

  html.prefers-contrast & {
    width: 80%;
    padding-block-end: 2vw;
    padding-inline: 0;
    button {
      width: 100% !important;
    }
  }
  /* button overrides */
  button,
  a,
  span {
    ${legend80};
    ${bold};
    padding-inline: 0 !important;
    cursor: pointer !important;
    ${getMediaQuery("m", { max: true })} {
      display: block;
    }

    &:hover {
      // specific style for those buttons
      color: rgb(var(--grey)) !important;
      fill: rgb(var(--grey)) !important;
    }
  }

  span.text {
    ${getMediaQuery("m", { max: true })} {
      display: flex;
      text-align: left;
      width: 100%;
      justify-content: flex-start;
    }
  }

  ${getMediaQuery("m")} {
    position: absolute;
    bottom: 0;
    left: ${props => (props.userGuide ? "50%" : "0")};
  }

  [dir="rtl"] & {
    left: auto;
    right: ${props => (props.userGuide ? "50%" : "0")};

    ${getMediaQuery("m")} {
      right: 50%;
    }

    ${props =>
      props.userGuide
        ? `
        transform: none;
        justify-content: center;
        left: 0;
        right: 0;
      `
        : ""}
    ${getMediaQuery("m")} {
      ${props =>
        props.userGuide
          ? `
        transform: none;
        justify-content: center;
        left: 0;
        right: 0;
      `
          : ""}
    }
  }
`

const CTA = styled.div`
  /* // button overrides */
  button,
  a {
    padding-inline: 0 !important;

    &:hover {
      // specific style for those buttons
      color: rgb(var(--grey)) !important;
      fill: rgb(var(--grey)) !important;
    }
  }

  /* ${getMediaQuery("m")} {
    bottom: 10%;
  } */
  ${getMediaQuery("m", { max: true })} {
    margin-left: 4%;
  }
  html.prefers-contrast & {
    width: 100%;
    a,
    span {
      width: 100% !important;
    }
  }
`

export default function DownloadCard(props) {
  const { title, subtitle, rmc, family_code: familyCode, picture_cld: picture, cta, file_path_cld: path, modal, downloadType, video_player_cld } = props

  const env = useEnv()
  const locale = useLocale()
  const dictionary = useDictionary()
  const { countryCode } = useUser()

  const lang = locale?.current?.codes?.www

  const [isModalOpen, setIsModalOpen] = useState(false)

  const bodyElement = process.browser && document.body

  const handleCardClick = async () => {
    if (downloadType === "wallpaper") setIsModalOpen(true)
    else if (downloadType === "brochure") {
      const modelData = await getModelData({ rmc, locale, env })
      const { price, currency } = await getPriceDatas({ rmc, countryCode, locale, env })

      waTriggerEvent({
        eventName: "download",
        eventType: "Click",
        products: [
          {
            productCategory: modelData?.familyNode?.code,
            productModel: modelData?.nameCode,
            productID: rmc,
            productQuantity: "1",
            productPrice: price,
            currency: currency,
          },
        ],
        downloadInfo: {
          position: "media page",
          type: downloadType.toLowerCase(),
          fileName: path,
          size: "",
        },
      })
    } else {
      waTriggerEvent({
        eventName: "download",
        eventType: "Click",
        products: [
          {
            productCategory: familyCode,
            productModel: "",
            productID: "",
            productQuantity: "",
            productPrice: "",
            currency: "",
          },
        ],
        downloadInfo: {
          position: "media page",
          type: downloadType.toLowerCase(),
          fileName: path,
          size: "",
        },
      })
    }
  }

  // console.log("downloadTYpe", props, env.content, path)

  const source = downloadType === "brochure" ? env.assets : `https://${env.cloudinary.secureDistribution}`

  const downloadUrl = () => {
    if (downloadType === "brochure") return `${source}/api/brochure/${lang}/${familyCode}/${rmc}.pdf`
    if (downloadType === "userGuide") return `${source}${path}`
    if (downloadType === "wallpaper") return null
    return null
  }

  const pictureProps = { ...picture, alt: null }

  return (
    <Root isBrochure={downloadType === "brochure"}>
      <Card
        as={downloadType !== "wallpaper" ? "a" : "button"}
        aria-label={`${title} - ${cta.text} ${dictionary[downloadType]()}`}
        onClick={downloadType !== "wallpaper" ? handleCardClick : () => setIsModalOpen(true)}
        href={downloadUrl()}
        target={downloadType !== "wallpaper" ? "_blank" : null}
      >
        <ImageWrapper downloadType={downloadType}>
          <Image {...pictureProps} key={picture.metadata?.uid} sizes='(min-width: 80rem) 33vw, 50vw' alt='' />
        </ImageWrapper>
        <Content downloadType={downloadType}>
          <Title>{title}</Title>
          {subtitle && <Description>{subtitle}</Description>}
        </Content>
      </Card>
      <CTAS userGuide={downloadType === "userGuide"}>
        {downloadType !== "userGuide" && (
          <CTA userGuide={false}>
            <Link aria_label={cta.text} tabIndex='-1' className='text small main-link' icon={cta.icon} onClick={handleCardClick} external={downloadUrl()}>
              {cta.text}
            </Link>
          </CTA>
        )}
        {downloadType === "userGuide" && (
          <>
            {video_player_cld?.sources?.length > 0 && <VideoLauncher icon='play' className='text small' video_player_cld={video_player_cld} />}
            <CTA userGuide={true}>
              <Link
                aria_label={cta.text}
                tabIndex='-1'
                className='text small'
                icon={cta.icon}
                external={`${source}${path}`}
                download={`${source}${path}`}
                onClick={handleCardClick}
              >
                {cta.text}
              </Link>
            </CTA>
          </>
        )}
      </CTAS>
      {process.browser &&
        createPortal(
          <AnimatePresence>
            {isModalOpen && (
              <Modal
                {...modal}
                rmc={rmc}
                family={familyCode}
                familyCode={familyCode}
                path={path}
                downloadType={downloadType}
                onClose={() => setIsModalOpen(false)}
              />
            )}
          </AnimatePresence>,
          bodyElement
        )}
    </Root>
  )
}
